import './reminder-list-item.scss';

import { isEmpty, kebabCase } from 'lodash';
import { useMemo } from 'react';

import { BaseReminderItemDataFragment } from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { ReminderDateDescription } from '../reminder-date-description';
import { ReminderDescription } from '../reminder-description';

type ReminderListItemProps = {
  reminder: BaseReminderItemDataFragment;
  onClick?: (value: BaseReminderItemDataFragment) => void;
};

export function ReminderListItem({ reminder, onClick }: ReminderListItemProps) {
  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(reminder),
        onKeyDown: makeKeyDownHandler(() => onClick(reminder)),
      };
    }

    return {};
  }, [reminder, onClick]);

  const { getFullName, getBirthDate } = useFormatters();

  return (
    <div
      {...attributes}
      className={`reminder-list-item${reminder.corrupt ? ' corrupt' : ''}`}
    >
      <div className="reminder-list-item__content">
        <h3>{reminder.conversation?.title}</h3>

        <p>
          <ReminderDescription
            data={{
              startDate: reminder.startDate,
              frequency: reminder.frequency,
              noEndDate: isEmpty(reminder.endDate),
              occurrence: reminder.occurrence,
            }}
          />
        </p>
      </div>
      <div className="reminder-list-item__content">
        {reminder.patient && (
          <>
            <h3>{getFullName(reminder.patient)}</h3>
            <p>{getBirthDate(reminder.patient)}</p>
          </>
        )}
      </div>
      <div className="reminder-list-item__content">
        {reminder.healthProfessional && (
          <>
            <h3>{getFullName(reminder.healthProfessional)}</h3>
            <p>
              <CustomFormattedMessage
                id={`common.honorific.${kebabCase(
                  reminder.healthProfessional.honorific
                )}`}
              />
            </p>
          </>
        )}
      </div>
      <div className="reminder-list-item__content">
        <h3>
          <CustomFormattedMessage id="common.reminder-date.title" />
        </h3>
        <p>
          <ReminderDateDescription
            data={{
              lastSendDate: reminder.lastSendDate,
              nextSendDate: reminder.nextSendDate,
            }}
          />
        </p>
      </div>
    </div>
  );
}
